'use strict'
const _ = require('lodash')
const {createPageManager} = require('../../helpers/pageManager')
const {migrateToNewMenuComponent, NEW_MENU} = require('../../helpers/oldMenuToNewMenuHelper')
const {
    migrateStylableComponentsByCompType
} = require('../../helpers/stylableMigration/migrateStylableComponentsByCompType')
const {NAMESPACE_MAPPING} = require('@wix/document-services-json-schemas').namespaceMapping

const {migrateMenuLayout} = require('./layout')
const {migrateSubmenuLayout} = require('./submenu')
const {migrateMegaMenuContainerItem} = require('./megaMenuContainerItem')
const {getComponentsByType, clearTemporaryProperties} = require('./utils')
const {migrateStructuredMegaMenuSubmenuDesign} = require('./structuredMegaMenuSubmenu')

const clearAllTemporaryProperties = ({styleProperties, propertiesOverride, propertiesSource}) => {
    clearTemporaryProperties(styleProperties)
    clearTemporaryProperties(propertiesOverride)
    clearTemporaryProperties(propertiesSource)
}

const migrateStylesAndLayoutPanelForEachBreakpoint = (oldComponent, pageManager) => {
    const styleKeys = pageManager.data.getItem(NAMESPACE_MAPPING.style, oldComponent.styleId)
    const cleanedPropsKey = oldComponent.propertyQuery.replace('#', '')
    const componentProps = pageManager.data.getItem(NAMESPACE_MAPPING.props, cleanedPropsKey)

    styleKeys.values.forEach(styleKey => {
        const cleanedStyleKey = styleKey.replace('#', '')
        const currentBreakpoint = pageManager.data.getItem(NAMESPACE_MAPPING.style, cleanedStyleKey)
        //Different breakoint than the Desktop/root
        if (currentBreakpoint.type === 'VariantRelation') {
            const variantKey = currentBreakpoint.to.replace('#', '')
            const variantStyle = pageManager.data.getItem(NAMESPACE_MAPPING.style, variantKey)?.style
            const variantStyleProperties = variantStyle?.properties
            const variantStylePropertiesOverride = variantStyle?.propertiesOverride
            const variantStylePropertiesSource = variantStyle?.propertiesSource
            migrateMenuLayout(variantStyleProperties, componentProps)
            migrateSubmenuLayout(variantStyleProperties, componentProps)
            migrateMegaMenuContainerItem(pageManager, oldComponent, variantStyleProperties, currentBreakpoint.variants)
            clearAllTemporaryProperties({
                styleProperties: variantStyleProperties,
                propertiesOverride: variantStylePropertiesOverride,
                propertiesSource: variantStylePropertiesSource
            })
        }
        //Desktop/root breakpoint
        if (currentBreakpoint.type === 'ComponentStyle') {
            const componentStyle = currentBreakpoint?.style
            const componentStyleProperties = componentStyle?.properties
            const componentStylePropertiesOverride = componentStyle?.propertiesOverride
            const componentStylePropertiesSource = componentStyle?.propertiesSource
            migrateMenuLayout(componentStyleProperties, componentProps)
            migrateSubmenuLayout(componentStyleProperties, componentProps)
            migrateMegaMenuContainerItem(pageManager, oldComponent, componentStyleProperties)
            clearAllTemporaryProperties({
                styleProperties: componentStyleProperties,
                propertiesOverride: componentStylePropertiesOverride,
                propertiesSource: componentStylePropertiesSource
            })
        }

        migrateStructuredMegaMenuSubmenuDesign(pageManager, oldComponent)
    })
}

const CSS_PROP_TO_VAR_NAME = {
    label: {
        'text-decoration-line': 'text-decoration',
        'background-color': 'text-highlight'
    },
    submenuItemHeadingRoot: {
        'background-color': 'background'
    },
    scrollButtonIcon: {
        display: 'icon-display',
        width: 'icon-size',
        height: 'icon-size',
        fill: 'icon-color',
        transform: 'icon-rotation'
    },
    submenuListWrapper: {
        'column-count': 'columns-number'
    },
    submenuPositionBox: {
        'margin-left': 'horizontal-margin'
    }
}

const STYLABLE_HORIZONTAL_MENU = 'wixui.StylableHorizontalMenu'

const migrateStylableHorizontalMenuToNonStylable = ({
    structure,
    data,
    uniqueIdGenerator,
    clientSpecMap,
    magicObject
}) => {
    migrateStylableComponentsByCompType({
        structure,
        data,
        uniqueIdGenerator,
        clientSpecMap,
        newSkin: NEW_MENU.SKIN,
        compType: STYLABLE_HORIZONTAL_MENU,
        useCssColor: true,
        withBorderRadiusAsOneParam: true,
        magicObject,
        postStyleMigrationCallback: style => {
            const {properties} = style
            if (properties['scroll-button-icon-display'] === 'initial') {
                properties['scroll-button-icon-display'] = 'unset'
            }
            return style
        },
        selectorsMappings: {
            '.root': {
                name: 'container'
            },
            '.root::menu': {
                name: 'menu'
            },
            '.root::menuItem': {
                name: 'item'
            },
            '.root::menuItem::container': {
                name: 'item-container',
                properties: CSS_PROP_TO_VAR_NAME.textAlign
            },
            '.root::menuItem:isCurrentPage': {
                name: 'item-selected',
                root: 'item'
            },
            '.root::menuItem:isHovered': {
                name: 'item-hover',
                root: 'item'
            },
            '.root::menuItem:hover': {
                name: 'item-hover',
                root: 'item'
            },
            '.root::menuItem::label': {
                name: 'item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::menuItem:isCurrentPage::label': {
                name: 'item-selected',
                root: 'item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::menuItem:isHovered::label': {
                name: 'item-hover',
                root: 'item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::menuItem:hover::label': {
                name: 'item-hover',
                root: 'item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::scrollButton': {
                name: 'scroll-button'
            },
            '.root::scrollButton:hover': {
                name: 'scroll-button-hover',
                root: 'scroll-button'
            },
            '.root::scrollButton::icon': {
                name: 'scroll-button',
                properties: CSS_PROP_TO_VAR_NAME.scrollButtonIcon
            },
            '.root::scrollButton:hover::icon': {
                name: 'scroll-button-hover',
                root: 'scroll-button',
                properties: CSS_PROP_TO_VAR_NAME.scrollButtonIcon
            },
            '.root::columnsLayout': {
                name: 'dropdown-container'
            },
            '.root::columnsLayout::heading::root': {
                name: 'dropdown-menu-item',
                properties: CSS_PROP_TO_VAR_NAME.submenuItemHeadingRoot
            },
            '.root::columnsLayout::heading': {
                name: 'dropdown-menu-item'
            },
            '.root::columnsLayout::heading:isHovered': {
                name: 'dropdown-menu-item-hover',
                root: 'dropdown-menu-item'
            },
            '.root::columnsLayout::heading::root:hover': {
                name: 'dropdown-menu-item-hover',
                root: 'dropdown-menu-item',
                properties: CSS_PROP_TO_VAR_NAME.submenuItemHeadingRoot
            },
            '.root::columnsLayout::heading::label': {
                name: 'dropdown-menu-item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::columnsLayout::heading::label:hover': {
                name: 'dropdown-menu-item-hover',
                root: 'dropdown-menu-item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::columnsLayout::menuItem': {
                name: 'dropdown-menu-sub-item'
            },
            '.root::columnsLayout::menuItem:isHovered': {
                name: 'dropdown-menu-sub-item-hover',
                root: 'dropdown-menu-sub-item'
            },
            '.root::columnsLayout::menuItem:isCurrentPage': {
                name: 'dropdown-menu-sub-item-selected',
                root: 'dropdown-menu-sub-item'
            },
            '.root::columnsLayout::menuItem::label': {
                name: 'dropdown-menu-sub-item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::columnsLayout::menuItem:isHovered::label': {
                name: 'dropdown-menu-sub-item-hover',
                root: 'dropdown-menu-sub-item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::columnsLayout::menuItem:isCurrentPage::label': {
                name: 'dropdown-menu-sub-item-selected',
                root: 'dropdown-menu-sub-item',
                properties: CSS_PROP_TO_VAR_NAME.label
            },
            '.root::columnsLayout::menuItem::container': {
                name: 'dropdown-menu'
            },
            '.root::columnsLayout::listWrapper': {
                name: 'dropdown-menu',
                properties: CSS_PROP_TO_VAR_NAME.submenuListWrapper
            },
            '.root::positionBox': {
                name: 'dropdown',
                properties: CSS_PROP_TO_VAR_NAME.submenuPositionBox
            },
            '.root::megaMenuWrapper': {
                name: 'mega-menu-wrapper'
            },
            '.root::containerPositionBox': {
                name: 'mega-menu-container-position-box'
            }
        }
    })
}

function migratePage({pageManager, structure, data, uniqueIdGenerator, clientSpecMap, magicObject}) {
    migrateStylableHorizontalMenuToNonStylable({structure, data, uniqueIdGenerator, clientSpecMap, magicObject})
    const {desktopComponents} = pageManager.structure.getAllComponentsByContainingChildType(STYLABLE_HORIZONTAL_MENU)

    _.forEach(desktopComponents, parentComponent => {
        const stylableHorizontalMenuComponents = getComponentsByType(
            parentComponent.components,
            STYLABLE_HORIZONTAL_MENU
        )

        _.forEach(stylableHorizontalMenuComponents, component => {
            migrateStylesAndLayoutPanelForEachBreakpoint(component, pageManager)
            migrateToNewMenuComponent(component)
        })
    })
}

module.exports = {
    name: 'migrateStylableHorizontalMenuToNewMenu',
    version: 0,
    experimentalVersions: [
        {version: 1, experiment: 'dm_migrateStylableHorizontalMenuToNewMenu'},
        {version: 2, experiment: 'dm_migrateBlocksWidgetsToNonStylable'}
    ],
    migrateStylableHorizontalMenuToNonStylable,
    exec(pageJson, _pageIdsArray, magicObject) {
        if (!magicObject.isExperimentOpen('dm_migrateStylableHorizontalMenuToNewMenu')) {
            return
        }
        const {clientSpecMap} = magicObject
        const {uniqueIdGenerator} = magicObject.dataFixerUtils
        const pageManager = createPageManager(pageJson, uniqueIdGenerator)
        const {data, structure} = pageJson

        migratePage({pageManager, structure, data, uniqueIdGenerator, clientSpecMap, magicObject})
    }
}
